import {useHostReady} from '@waka/compat/host-ready';
import {requestHost} from '@waka/host/client';
import { ApiResponse, CustomQueryOptions } from '@/shared/types';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
export { PremiumVendors } from '@/shared/lib/tts-voice-display';

export const DtoVendorSchema = z.enum([
  'claude',
  'gemini',
  'openai',
  'typecast',
  'sonix',
  'wakawaka',
  'pyannote',
  'elevenlabs',
]);

export type DtoVendor = z.infer<typeof DtoVendorSchema>;

export const TTSVoiceEmotionSchema = z.object({
  code: z.string(),
  name: z.string(),
  nameKorean: z.string(),
});

export const VideoEditorTTSVoiceSchema = z.object({
  code: z.string(),
  name: z.string(),
  nameKorean: z.string(),
  description: z.string(),
  descriptionKorean: z.string(),
  thumbnailUrl: z.string().optional(),
  vendor: DtoVendorSchema,
  vendorModel: z.string().optional(),
  audioModel: z.string().optional(),
  emotions: z.array(TTSVoiceEmotionSchema).default([]),
  tempoMin: z.number().default(0.8),
  tempoMax: z.number().default(1.5),
  tempoDefault: z.number().default(1.0),
  pitchMin: z.number().nullable().default(null),
  pitchMax: z.number().nullable().default(null),
  isNew: z.boolean().default(false),
  defaultUseCases: z.array(z.string()).default([]),
  recommended: z.boolean().default(false),
  recommendationOrder: z.number().int().nullable().default(null),
});

export type TTSEmotion = z.infer<typeof TTSVoiceEmotionSchema>;
export type VideoEditorTTSVoice = z.infer<typeof VideoEditorTTSVoiceSchema>;

type ResponseType = ApiResponse<VideoEditorTTSVoice[]>;

export type GetTTSVoicesParams = {
  includeSpaceRecommendations?: boolean;
  /**
   * 화면 operator 기능(useFeatureEnabled)이 켜졌을 때만 붙이는 전체 조회 축.
   * 실제 권한은 be-api 가 토큰 role 로 재검증하므로, FE 는 노출 의도만 전달한다
   * (파라미터 조작은 서버가 무시 — use-deck-presets.ts 패턴 동일).
   */
  includeAll?: boolean;
};

export const getTTSVoicesApi = async (_params?: GetTTSVoicesParams) => {
  try {
    const result=await requestHost({method:'GET',path:'/__waka/voices'});
    return z.object({status:z.string(),code:z.number(),message:z.string(),data:z.array(VideoEditorTTSVoiceSchema)}).parse(result);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const QueryKeyUseGetVideoEditorTTSVoicesApi =
  'getVideoEditorData' as const;

type UseGetTTSVoicesApiOptions = Omit<
  CustomQueryOptions<ResponseType>,
  'enabled'
> & {
  includeSpaceRecommendations?: boolean;
  /**
   * v5 UseQueryOptions.enabled 는 함수형(`(query) => boolean`)도 허용하지만,
   * 이 훅은 isOperatorLoading 과 `&&` 로 결합해 boolean 으로 강등시키므로
   * 함수형을 받으면 조용히 무시된다 — 타입에서 원천 배제한다.
   */
  enabled?: boolean;
};

export const useGetTTSVoicesApi = (
  queryOptions?: UseGetTTSVoicesApiOptions,
) => {
  const { queryKey, includeSpaceRecommendations, ...rest } = queryOptions ?? {};

  // Wait for the authorized Waka work, not a company token/role that this port never receives.
  const ready=useHostReady();

  const query = useQuery({
    queryKey: [
      QueryKeyUseGetVideoEditorTTSVoicesApi,
      includeSpaceRecommendations ?? false,
      ready,
      ...(queryKey ?? []),
    ],
    queryFn: () =>
      getTTSVoicesApi({ includeSpaceRecommendations }),
    staleTime: 5 * 60 * 1000,
    ...rest,
    enabled: (rest.enabled ?? true) && ready,
  });

  return {
    ...query,
    // An unconnected work is not an empty, successfully loaded catalog.
    isLoading: query.isLoading || !ready,
  };
};
